// @ts-strict-ignore
import { BufferAttribute, BufferGeometry, Group, LineBasicMaterial, LineSegments, Material, Mesh, PlaneGeometry, Vector2, Vector3 } from 'three';
import { DimensionElement } from '@3d-scene/layer/elements/dimension-element';
import { StringUtils } from '@shared/util/string-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { SimpleTextValueVO } from '@shared/vo/meta-element.vo';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { LabelElement } from '@3d-scene/layer/elements/label-element';

export class DimensionUtils {

  private static readonly DEFAULT_FONT_SIZE: number = 14;
  private static readonly DEFAULT_FONT_SCALE: number = 3;

  public static getTextWidth(text: SimpleTextValueVO, fontSize: number = NaN, fontScale: number = NaN): number {
    return this.getTextSize(text.text, fontSize, fontScale).x;
  }

  public static getTextHeight(text: SimpleTextValueVO, fontSize: number = NaN, fontScale: number = NaN): number {
    return this.getTextSize(text.text, fontSize, fontScale).y;
  }

  public static getTextSize(text: string, fontSize: number = 14, fontScale: number = 1): Vector2 {
    if (StringUtils.isEmpty(text)) {
      return new Vector2();
    }

    fontSize = NumberUtils.isNotNumber(fontSize) ?  DimensionUtils.DEFAULT_FONT_SIZE : fontSize;
    fontScale = NumberUtils.isNotNumber(fontScale) ?  DimensionUtils.DEFAULT_FONT_SCALE : fontScale;

    const context: CanvasRenderingContext2D = TextureUtils.createCanvasContext();
    TextureUtils.applyDefaultFontToContext(context, fontSize, fontScale);
    const textMetrics: TextMetrics = context.measureText(text);
    return new Vector2(textMetrics.width, fontSize * fontScale);
  }

  public static getTextLength(text: string): number {
    const extent: number = 10;
    const fontSize: number = 78; // this value is taken from flex
    return this.getTextSize(text, fontSize).x + extent;
  }

  public static isDimensionNeedsOuterLabel(start: Vector3, end: Vector3, textWidth: number): boolean {
    return start.distanceTo(end) - (DimensionElement.LINE_THICKNESS * 10) * 2 < textWidth;
  }

  public static createBackground(width: number, heigth: number, backgroundMaterial: Material, position: Vector3): Mesh {
    const mesh: Mesh = new Mesh(new PlaneGeometry(width, heigth), backgroundMaterial);
    mesh.rotation.x = MathUtils.toRadians(LabelElement.DEFAULT_ROTATION.x);
    mesh.rotation.y = MathUtils.toRadians(LabelElement.DEFAULT_ROTATION.y);
    mesh.rotation.z = MathUtils.toRadians(LabelElement.DEFAULT_ROTATION.z);
    mesh.position.set(position.x, position.y, position.z);
    return mesh;
  }

  public static createLineSegment(startPoint: Vector3, endPoint: Vector3, color): LineSegments {
    const positions = new Float32Array([
      startPoint.x, startPoint.y, startPoint.z,
      endPoint.x, endPoint.y, endPoint.z
    ]);
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    return new LineSegments(geometry, new LineBasicMaterial({color: color}));
  }

  /**
   * Creates a bracket (\__________/ shape) between two arbitrary 3D points.
   * @param start The start point (Vector3).
   * @param end The end point (Vector3).
   * @param angleRadians the rotation around the start-end-line
   */
  public static createBracket(start: Vector3, end: Vector3, angleRadians: number) {
    const offset = 15;

    const dir = new Vector3().subVectors(end, start).normalize();
    const up = new Vector3(0, 1, 0);
    if (Math.abs(dir.dot(up)) > 0.99) up.set(1, 0, 0);

    const perpendicular = new Vector3().crossVectors(dir, up).normalize();

    const offsetStart = perpendicular.clone().multiplyScalar(offset).add(dir.clone().multiplyScalar(offset));
    const offsetEnd = perpendicular.clone().multiplyScalar(offset).add(dir.clone().multiplyScalar(-offset));

    const cornerStart = new Vector3().addVectors(start, offsetStart);
    const cornerEnd = new Vector3().addVectors(end, offsetEnd);

    const lineMiddle = DimensionUtils.createLineSegment(cornerStart, cornerEnd, 0x000000)
    const lineStart = DimensionUtils.createLineSegment(start, cornerStart, 0x000000);
    const lineEnd = DimensionUtils.createLineSegment(end, cornerEnd, 0x000000);

    const group = new Group()
    group.add(lineStart, lineMiddle, lineEnd)

    MathUtils.rotateObjectAroundLine(group, start, end, angleRadians)

    return group;
  }
}
